import type { ChatResponse, Blueprint, SearchResult, UserSessionsResponse } from '@/types';
import { apiFetch } from '../lib/api';

/** ai-service agent endpoint */
const AI_BASE_URL = '/ai/v1';
/** pilot-mcp blueprint API (proxied via /mcp → pilot-mcp:3003/api) */
const MCP_BASE_URL = '/mcp';

class ApiService {
  /**
   * Chat with pilot scenario via ai-service agent loop.
   * The agent autonomously decides when to extract requirements, generate blueprints, etc.
   */
  async chat(request: { message: string; sessionId?: string }): Promise<ChatResponse> {
    const messages = [{ role: 'user' as const, content: request.message }];

    const response = await apiFetch(`${AI_BASE_URL}/skills/agent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages,
        scenarioId: 'pilot',
        systemPrompt: undefined, // use scenario default
        maxIterations: 10,
      }),
    });

    if (!response.ok) {
      throw new Error(`Chat failed: ${response.statusText}`);
    }

    const agentResult = await response.json();

    // Transform AgentResult → ChatResponse format
    return {
      reply: agentResult.content || '',
      sessionId: request.sessionId || '',
      isComplete: false,
    };
  }

  async getBlueprints(): Promise<Blueprint[]> {
    const response = await apiFetch(`${MCP_BASE_URL}/blueprints`);
    if (!response.ok) {
      throw new Error(`Failed to fetch blueprints: ${response.statusText}`);
    }
    return response.json();
  }

  async getBlueprint(id: string): Promise<Blueprint> {
    const response = await apiFetch(`${MCP_BASE_URL}/blueprints/${id}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch blueprint: ${response.statusText}`);
    }
    return response.json();
  }

  async searchBlueprints(query: string): Promise<SearchResult[]> {
    const response = await apiFetch(`${MCP_BASE_URL}/knowledge/search?q=${encodeURIComponent(query)}`);
    if (!response.ok) {
      throw new Error(`Search failed: ${response.statusText}`);
    }
    return response.json();
  }

  async semanticSearch(query: string, mode?: 'vector' | 'keyword' | 'hybrid' | 'auto'): Promise<SearchResult[]> {
    const params = new URLSearchParams({ q: query });
    if (mode) params.append('strategy', mode);

    const response = await apiFetch(`${MCP_BASE_URL}/knowledge/semantic-search?${params.toString()}`);
    if (!response.ok) {
      throw new Error(`Semantic search failed: ${response.statusText}`);
    }
    const data = await response.json();
    return data.results || [];
  }

  async deleteBlueprint(id: string): Promise<void> {
    const response = await apiFetch(`${MCP_BASE_URL}/blueprints/${id}`, { method: 'DELETE' });
    if (!response.ok) {
      throw new Error(`Failed to delete blueprint: ${response.statusText}`);
    }
  }

  async getSessionBlueprints(sessionId: string): Promise<Blueprint[]> {
    const response = await apiFetch(`${MCP_BASE_URL}/sessions/${sessionId}/blueprints`);
    if (!response.ok) {
      throw new Error(`Failed to fetch session blueprints: ${response.statusText}`);
    }
    const data = await response.json();
    return data.blueprints || [];
  }

  /**
   * Get conversations from ai-service (scenarioId=pilot).
   * Uses ai-service's conversation endpoint.
   */
  async getUserSessions(): Promise<UserSessionsResponse> {
    const response = await apiFetch(`${AI_BASE_URL}/conversations?scenarioId=pilot`);
    if (!response.ok) {
      throw new Error(`Failed to fetch sessions: ${response.statusText}`);
    }
    // この口が返すのは配列ではなく `{ items, total, page, limit, totalPages }`。
    // 以前は data をそのまま配列として扱っており `data.map is not a function`
    // で毎回失敗していた。**保存はできているのに一覧だけ空**という
    // 分かりにくい壊れ方をしていたのはこれが原因。
    const data = await response.json();
    const items = Array.isArray(data) ? data : (data.items ?? []);
    return {
      count: typeof data.total === 'number' ? data.total : items.length,
      sessions: items.map((conv: any) => ({
        sessionId: conv._id || conv.id,
        title: conv.title,
        createdAt: conv.createdAt,
        lastMessageAt: conv.updatedAt || conv.createdAt,
        messageCount: conv.messages?.length || 0,
        status: 'active',
      })),
    };
  }

  /**
   * Get session details.
   * TODO: Phase 2 — properly integrate with ai-service conversation model.
   * For now returns a stub to avoid breaking ChatPage.
   */
  async getSession(sessionId: string, _tempUserId?: string): Promise<{ messages: any[]; id: string }> {
    try {
      const response = await apiFetch(`${AI_BASE_URL}/conversations/${sessionId}`);
      if (!response.ok) return { id: sessionId, messages: [] };
      const data = await response.json();
      return { id: sessionId, messages: data.messages || [] };
    } catch {
      return { id: sessionId, messages: [] };
    }
  }

  async deleteSession(sessionId: string): Promise<void> {
    const response = await apiFetch(`${AI_BASE_URL}/conversations/${sessionId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`Failed to delete session: ${response.statusText}`);
    }
  }
}

export const apiService = new ApiService();
